import Phaser from 'phaser';
import { Health } from '@/entities/Health';
import { boxFromCenter, type Box } from '@/systems/box';
import type { Combatant, HitSpec, Team } from '@/systems/Combat';
import { AnimationStateMachine, type AnimState } from '@/systems/AnimationStateMachine';
import { LANDING_HITSTUN, type ThrownState } from '@/data/throws';
import { tickThrownArc } from '@/systems/ThrowSystem';
import type { WeaponState } from '@/data/weapons';
import {
  beginJuggle,
  tickJuggle,
  juggleHitAllowed,
  juggleHitstun,
  applyJuggleHit,
  JUGGLE_LANDING_HITSTUN,
  type JuggleState,
} from '@/systems/JuggleState';
import { resolveEntityTexture } from '@/systems/entitySpriteResolver';
import { resolvePoseTexture, hasKoPoseSprite } from '@/systems/poseResolver';
import { spriteAtlas } from '@/systems/SpriteAtlas';
import { spriteAnims, animKey as toAnimKey } from '@/systems/SpriteAnimRegistry';
import { locomotionTransform, actionTransform } from '@/systems/spriteAnim';

export interface EntityOptions {
  maxHp: number;
  /** Placeholder body colour until sprite art lands. */
  color: number;
  /** Pixels per frame at 60fps. */
  speed: number;
  bodyW?: number;
  bodyH?: number;
  /** Short label drawn on the placeholder rect (e.g. "RAY") until Soul-ID art lands. */
  label?: string;
  /** Sprite atlas assetId — renders the texture when loaded; falls back to colour body. */
  assetId?: string;
  /**
   * Per-state pose sprite overrides (MY-1951). When an AnimState key is present
   * and its assetId is loaded in the SpriteAtlas, that texture is shown instead
   * of the base `assetId`. Composes with procedural locomotion transforms.
   */
  poseAssetIds?: Partial<Record<AnimState, string>>;
  /**
   * Initial armour HP pool (MY-1886). When set, the JuggernautArmour system
   * absorbs all damage until the pool is depleted before HP takes any hit.
   */
  armourPool?: number;
  /** Enable gang-tactics layer (MY-1887): surround spread + lone low-HP retreat. */
  groupTactics?: boolean;
  /** Weak PM Halloway coda behaviour (MY-1978): cower/flee and deal no damage. */
  pmHalloway?: boolean;
  /**
   * Light-poise threshold (MY-2414). Enemy shrugs off hitstun after this many
   * consecutive light hits. Computed from tier via getDefaultPoiseThreshold()
   * when not set explicitly.
   */
  lightPoiseThreshold?: number;
}

let nextCombatId = 1;

/**
 * Entity — base for every fighter (players, enemies, bosses).
 *
 * Wraps a placeholder body rectangle + drop shadow in a Container so the
 * whole fighter moves as a unit. Implements Combatant so the CombatSystem
 * can resolve hits against it. Sprite swaps (art pipeline) replace `bodyRect`.
 */
export abstract class Entity extends Phaser.GameObjects.Container implements Combatant {
  readonly health: Health;

  readonly combatId: number = nextCombatId++;

  abstract readonly team: Team;

  facing: 1 | -1 = 1;

  speed: number;

  isKO = false;

  /** Total visual body height in pixels (used externally, e.g. floating HUD bars). */
  get entityHeight(): number {
    return this.bodyH;
  }

  /** Original body fill colour — used by subclasses for parry flash restore. */
  protected readonly bodyColor: number;

  /** Frames remaining of hit-stun (controls suppressed while > 0). */
  protected hitstun = 0;

  /** Height off the ground plane (px). 0 = grounded. Visual only.
   *  Named `elevation` not `z` — Phaser Container.z is public + reserved. */
  protected elevation = 0;

  private jumpVel = 0;

  private airborne = false;

  /** Non-null while this entity is flying as a thrown projectile. */
  protected thrownState: ThrownState | null = null;

  /** Non-null while this entity is in an airborne juggle arc. */
  private juggleState: JuggleState | null = null;

  /** Currently held weapon, or null if unarmed. */
  heldWeapon: WeaponState | null = null;

  private weaponIndicatorRect: Phaser.GameObjects.Rectangle | null = null;

  private readonly animSM = new AnimationStateMachine();

  /** Monotonic frame counter for procedural locomotion animation. */
  private _tFrames = 0;

  /** Frames elapsed in the current anim state (0 on the entry frame). */
  private _tInState = 0;

  /** True in syncAnim when animSM state changed this tick; consumed by applyLocomotionAnim. */
  private _animStateChanged = false;

  /** Per-state pose sprite overrides. See EntityOptions.poseAssetIds. */
  private readonly _poseAssetIds: Partial<Record<AnimState, string>> | undefined;

  /** Base assetId stored for pose fallback resolution. */
  private readonly _baseAssetId: string | undefined;

  /** Last texture key applied to bodyImage — guards against per-frame setTexture thrash. */
  private _currentBodyTexKey: string | null = null;

  /**
   * Base display scale of bodyImage after aspect-fit sizing. Locomotion squash/
   * stretch (`xf.scaleX/scaleY`, ~1.0 multipliers) must MULTIPLY these, never
   * overwrite — the image's native texture is ~896x1200 so its base scale is
   * tiny (~0.1); assigning a ~1.0 multiplier absolutely would render a 10x smear.
   */
  private _baseScaleX = 1;
  private _baseScaleY = 1;

  protected readonly bodyW: number;

  protected readonly bodyH: number;

  /** Horizontal world bound for clamping (defaults to screen; set per stage). */
  protected worldWidth: number;

  /** Named bodyRect (NOT `body`) to avoid clashing with Arcade Physics. */
  protected bodyRect: Phaser.GameObjects.Rectangle;

  /** Sprite image rendered when a texture is available; null = colour-body fallback. */
  protected bodyImage: Phaser.GameObjects.Image | null = null;

  /** Animated pixel sprite-sheet (RD anims); when set it takes priority over bodyImage/bodyRect. */
  protected bodySprite: Phaser.GameObjects.Sprite | null = null;

  /** Last anim key played on bodySprite — guards against per-frame replay thrash. */
  private _currentAnimKey: string | null = null;

  /**
   * Frames remaining on a bespoke one-shot clip (super/special). While > 0,
   * applyPoseTexture leaves the playing clip untouched so syncAnim's pose
   * selection can't overwrite it. Released early when the SM resolves to hurt/ko.
   */
  protected oneShotAnimLock = 0;

  protected shadow: Phaser.GameObjects.Ellipse;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: EntityOptions) {
    super(scene, x, y);
    this.health = new Health(opts.maxHp);
    this.speed = opts.speed;
    this.bodyW = opts.bodyW ?? 60;
    this.bodyH = opts.bodyH ?? 120;
    this.worldWidth = scene.scale.width;
    this._baseAssetId = opts.assetId;
    this._poseAssetIds = opts.poseAssetIds;

    this.bodyColor = opts.color;
    this.shadow = scene.add.ellipse(0, this.bodyH / 2, this.bodyW * 1.1, 18, 0x000000, 0.35);
    this.bodyRect = scene.add
      .rectangle(0, 0, this.bodyW, this.bodyH, opts.color)
      .setStrokeStyle(2, 0xffffff);

    this.add([this.shadow, this.bodyRect]);
    if (opts.label) {
      const labelText = scene.add
        .text(0, 0, opts.label, { fontSize: '13px', color: '#ffffff', align: 'center' })
        .setOrigin(0.5);
      this.add(labelText);
    }

    // Animated pixel sprite-sheet path (RD anims) — takes priority over static art.
    // When the char has sheets, build a Phaser Sprite and play its idle anim; the
    // bodyRect/bodyImage fallback paths are skipped entirely.
    if (spriteAnims.hasChar(opts.assetId)) {
      const idleKey = spriteAnims.resolve(opts.assetId, 'idle');
      const sheetState = spriteAnims.resolveSheetState(opts.assetId, 'idle');
      const e0 =
        opts.assetId && sheetState ? spriteAnims.getEntry(opts.assetId, sheetState) : undefined;
      if (idleKey && scene.textures.exists(idleKey)) {
        // Square RD frames (fw===fh); size by height like bodyImage, keep aspect.
        const aspect = e0 && e0.fh > 0 ? e0.fw / e0.fh : 1;
        const dispH = this.bodyH * 1.7;
        this.bodySprite = scene.add
          .sprite(0, this.bodyH / 2, idleKey)
          .setOrigin(0.5, 1) // feet on the shadow line
          .setDisplaySize(dispH * aspect, dispH);
        this._baseScaleX = this.bodySprite.scaleX;
        this._baseScaleY = this.bodySprite.scaleY;
        this.bodySprite.play(idleKey);
        this._currentAnimKey = idleKey;
        this.bodyRect.setVisible(false);
        this.add(this.bodySprite);
      }
    }

    const texKey = this.bodySprite ? null : resolveEntityTexture(opts.assetId, spriteAtlas);
    if (texKey !== null) {
      // Size by HEIGHT preserving the texture's native aspect (full-body art is
      // ~896x1200 = 0.75) — never force the box aspect (would squash). Sprite
      // height ~1.6x the hitbox so the margined figure reads at ~body height.
      const entry = spriteAtlas.getEntry(texKey);
      const aspect = entry && entry.h > 0 ? entry.w / entry.h : 0.75;
      const dispH = this.bodyH * 1.6;
      const dispW = dispH * aspect;
      this.bodyImage = scene.add
        .image(0, 0, texKey)
        .setOrigin(0.5, 1) // anchor feet to the shadow line
        .setDisplaySize(dispW, dispH);
      this._baseScaleX = this.bodyImage.scaleX;
      this._baseScaleY = this.bodyImage.scaleY;
      this.bodyRect.setVisible(false);
      this.add(this.bodyImage);
      this._currentBodyTexKey = texKey;
    }

    scene.add.existing(this);
  }

  /** Set the horizontal world bound (level width) for movement/knockback clamping. */
  setWorldWidth(width: number): void {
    this.worldWidth = width;
  }

  /** Ground-plane Y (feet), used for depth-sort + collision — NOT the jumped Y. */
  get groundY(): number {
    return this.y;
  }

  get isBeingThrown(): boolean {
    return this.thrownState !== null;
  }

  get isAirborne(): boolean {
    return this.airborne || this.isBeingThrown || this.juggleState !== null;
  }

  /** True while this entity is mid-juggle-arc (launched by a launcher hit). */
  get isAirborneJuggled(): boolean {
    return this.juggleState !== null;
  }

  /** True when in hitstun and not already airborne as a thrown projectile. */
  get isStunned(): boolean {
    return this.isHitstunned && !this.isBeingThrown;
  }

  getThrownState(): ThrownState | null {
    return this.thrownState;
  }

  beginThrow(state: ThrownState): void {
    this.thrownState = state;
    this.hitstun = 9999; // suppresses controls; reset to LANDING_HITSTUN on touch-down
  }

  /** Current animation state (drives sprite playback once art lands). */
  get animState(): AnimState {
    return this.animSM.current;
  }

  /**
   * Recompute the animation state from current condition + the subclass-supplied
   * action flags. KO/hurt/airborne are read from the entity; the rest are passed.
   * Also ticks the procedural locomotion layer every call (once per frame).
   */
  protected syncAnim(opts: {
    moving?: boolean;
    running?: boolean;
    attacking?: boolean;
    attackState?: 'attackL' | 'attackH';
    throwing?: boolean;
    special?: boolean;
    blocking?: boolean;
    taunting?: boolean;
  }): void {
    const { state, changed } = this.animSM.update({
      ko: this.isKO,
      hurt: this.isHitstunned,
      airborne: this.isAirborne,
      moving: opts.moving ?? false,
      running: opts.running ?? false,
      attacking: opts.attacking ?? false,
      attackState: opts.attackState,
      throwing: opts.throwing ?? false,
      special: opts.special ?? false,
      blocking: opts.blocking ?? false,
      taunting: opts.taunting ?? false,
    });
    if (changed) {
      this._animStateChanged = true;
      this.onAnimChange(state);
    }
    this.applyLocomotionAnim();
  }

  /**
   * Apply procedural locomotion offsets on top of the current elevation y.
   * Called each time syncAnim runs (once per frame). Composes with syncZVisual:
   * elevation offset is applied first by syncZVisual; this adds the anim delta.
   */
  protected applyLocomotionAnim(): void {
    // Reset tInState on the entry frame of a new state; increment each subsequent frame.
    if (this._animStateChanged) {
      this._tInState = -1;
      this._animStateChanged = false;
    }
    this._tInState += 1;
    this._tFrames += 1;
    if (this.oneShotAnimLock > 0) this.oneShotAnimLock -= 1;

    this.applyPoseTexture();

    const state = this.animSM.current;
    const locoXf = locomotionTransform(state, this._tFrames, this.facing);
    const actXf = actionTransform(state, this._tInState, this.facing);

    // Compose: locomotionTransform returns identity for action states; actionTransform
    // returns identity for locomotion states — so direct multiply/add works for both.
    const dx = actXf.dx;
    const dy = locoXf.dy + actXf.dy;
    const scaleX = locoXf.scaleX * actXf.scaleX;
    const scaleY = locoXf.scaleY * actXf.scaleY;
    const angleDeg = locoXf.angleDeg + actXf.angleDeg;

    this.bodyRect.x = dx;
    this.bodyRect.y = -this.elevation + dy;
    if (this.bodySprite) {
      // Real frame animation carries bob/squash/lean — skip scale/angle from procedural xf.
      // Apply dx lunge and elevation; flip for facing only.
      this.bodySprite.x = dx;
      this.bodySprite.y = -this.elevation + dy + this.bodyH / 2;
      this.bodySprite.scaleX = this.facing * this._baseScaleX;
      this.bodySprite.scaleY = this._baseScaleY;
    } else if (this.bodyImage) {
      this.bodyImage.x = dx;
      this.bodyImage.y = -this.elevation + dy + this.bodyH / 2;
      this.bodyImage.scaleX = this.facing * this._baseScaleX * scaleX;
      this.bodyImage.scaleY = this._baseScaleY * scaleY;
      if (!this.isKO) this.bodyImage.setAngle(angleDeg);
    } else {
      this.bodyRect.scaleX = this.facing * scaleX;
      this.bodyRect.scaleY = scaleY;
      if (!this.isKO) this.bodyRect.setAngle(angleDeg);
    }
  }

  /**
   * Resolve and apply the pose-sprite texture for the current anim state.
   * Only calls setTexture when the chosen key differs from the last applied key,
   * preventing per-frame thrash. No-op when bodyImage is null.
   */
  private applyPoseTexture(): void {
    if (this.bodySprite) {
      const cur = this.animSM.current;
      // Hard interrupts always win: a hurt/ko state releases any one-shot lock.
      if (cur === 'hurt' || cur === 'ko') this.oneShotAnimLock = 0;
      // While a bespoke one-shot (super/special) is held, leave the clip playing.
      if (this.oneShotAnimLock > 0) return;
      // Pick the best sheet for the current anim state and (re)play it on change.
      const animKey = spriteAnims.resolve(this._baseAssetId, cur);
      if (animKey && animKey !== this._currentAnimKey) {
        this._currentAnimKey = animKey;
        this.bodySprite.play(animKey);
      }
      return;
    }
    if (!this.bodyImage) return;
    const key = resolvePoseTexture(
      this.animSM.current,
      this._poseAssetIds,
      this._baseAssetId,
      spriteAtlas,
    );
    if (key === this._currentBodyTexKey) return;
    this._currentBodyTexKey = key;
    if (key !== null) {
      this.bodyImage.setTexture(key);
    }
  }

  /**
   * Play a bespoke one-shot clip (super/special) on the body sprite and lock it
   * for its natural run-time so syncAnim's pose selection can't overwrite it.
   * No-op if there is no body sprite or the anim isn't registered — the caller's
   * recovery-driven fallback pose then renders. Released early by hurt/ko in
   * applyPoseTexture(), so getting hit mid-clip still flips to hurt/ko.
   */
  protected playOneShot(key: string): void {
    if (!this.bodySprite || !this.scene.anims.exists(key)) return;
    this.bodySprite.play(key);
    this._currentAnimKey = key;
    const anim = this.scene.anims.get(key);
    const frames = anim?.frames.length ?? 12;
    const fps = anim && anim.frameRate > 0 ? anim.frameRate : 16;
    // Hold the clip for its real run-time, expressed in 60fps game frames (+2 margin).
    this.oneShotAnimLock = Math.ceil((frames / fps) * 60) + 2;
  }

  /**
   * Play a bespoke manifest sheet-state (e.g. 'super', 'special_glasgowKiss') as a
   * locked one-shot, keyed by this entity's own base asset id so the key always
   * matches what BootScene registered. No-op when the sheet was never registered.
   */
  protected playSheetState(sheetState: string): void {
    if (this._baseAssetId === undefined) return;
    this.playOneShot(toAnimKey(this._baseAssetId, sheetState));
  }

  /** Hook for the sprite adapter to swap clips on state change. No-op default. */
  protected onAnimChange(_state: AnimState): void {
    // Placeholder bodies have no spritesheet yet; art-pipeline tickets wire this.
  }

  /** Begin a jump if grounded. `power` = initial upward velocity (px/frame). */
  startJump(power: number): void {
    if (this.airborne || this.isKO || this.isBeingThrown || this.juggleState !== null) return;
    this.airborne = true;
    this.jumpVel = power;
  }

  /** Advance the jump arc one frame. Call from subclass update(). */
  protected tickJump(): void {
    if (!this.airborne) return;
    const GRAVITY = 0.6;
    this.elevation += this.jumpVel;
    this.jumpVel -= GRAVITY;
    if (this.elevation <= 0) {
      this.elevation = 0;
      this.airborne = false;
      this.jumpVel = 0;
    }
    this.syncZVisual();
  }

  /**
   * Advance the throw arc one frame when this entity is in flight.
   * Moves x by vx, updates elevation, applies landing damage + hitstun on touch-down.
   * Call from subclass update() before any AI/input logic.
   * @returns true while the entity is still airborne.
   */
  protected tickThrown(): boolean {
    if (!this.thrownState) return false;
    const state = this.thrownState;
    this.x = Phaser.Math.Clamp(this.x + state.vx, 40, this.worldWidth - 40);
    const stillAirborne = tickThrownArc(state);
    this.elevation = state.elevation;
    this.syncZVisual();
    if (!stillAirborne) {
      const dmg = state.landingDamage;
      this.thrownState = null;
      this.hitstun = LANDING_HITSTUN;
      this.elevation = 0;
      this.syncZVisual();
      if (!this.isKO) {
        this.health.damage(dmg);
        this.flash();
        if (this.health.isDead) this.knockOut();
      }
    }
    return stillAirborne;
  }

  /**
   * Advance the juggle arc one frame when this entity is mid-juggle.
   * Call from subclass update() after tickThrown().
   * @returns true while still airborne (caller should early-exit to skip AI/input).
   */
  protected tickJuggleArc(): boolean {
    if (!this.juggleState) return false;
    const stillAirborne = tickJuggle(this.juggleState);
    this.elevation = this.juggleState.elevation;
    this.syncZVisual();
    if (!stillAirborne) {
      this.juggleState = null;
      this.elevation = 0;
      this.hitstun = JUGGLE_LANDING_HITSTUN;
      this.syncZVisual();
      return false;
    }
    return true;
  }

  /** Lift the body sprite by z; shrink + fade the shadow as it rises. */
  protected syncZVisual(): void {
    this.bodyRect.y = -this.elevation;
    if (this.bodyImage) this.bodyImage.y = -this.elevation;
    if (this.bodySprite) this.bodySprite.y = -this.elevation + this.bodyH / 2;
    const shrink = 1 - Math.min(this.elevation / 240, 0.5);
    this.shadow.setScale(shrink, shrink);
    this.shadow.setAlpha(0.35 * (1 - Math.min(this.elevation / 320, 0.6)));
  }

  /** World-space hurtbox (AABB) derived from body size + position. */
  worldHurtbox(): Box {
    return boxFromCenter(this.x, this.y, this.bodyW, this.bodyH);
  }

  /** Combatant hook — apply an incoming hit. */
  receiveHit(spec: HitSpec, fromCenterX: number): void {
    if (this.isKO) return;
    const dir = this.x >= fromCenterX ? 1 : -1;

    if (this.juggleState) {
      // Aerial follow-up: apply diminishing hitstun; ignore when cap is reached.
      if (!juggleHitAllowed(this.juggleState)) return;
      const stunFrames = juggleHitstun(this.juggleState);
      applyJuggleHit(this.juggleState);
      this.takeDamage(spec.damage, dir * Math.round(spec.knockback * 0.3));
      this.hitstun = stunFrames;
      return;
    }

    this.takeDamage(spec.damage, dir * spec.knockback);
    this.hitstun = Math.max(this.hitstun, spec.hitstun);

    // A launcher hit on a grounded (non-thrown) target starts a juggle arc.
    if (spec.launcher && !this.isKO && !this.isBeingThrown) {
      this.juggleState = beginJuggle();
    }
  }

  get isHitstunned(): boolean {
    return this.hitstun > 0;
  }

  /** Decrement hit-stun; call once per frame from subclass update(). */
  protected tickHitstun(): void {
    if (this.hitstun > 0) this.hitstun -= 1;
  }

  /** Take damage + optional horizontal knockback. KO at 0 HP. */
  takeDamage(amount: number, knockbackX = 0): void {
    if (this.isKO) return;
    this.health.damage(amount);
    this.flash();
    if (knockbackX !== 0) {
      this.x = Phaser.Math.Clamp(this.x + knockbackX, 40, this.worldWidth - 40);
    }
    if (this.health.isDead) this.knockOut();
  }

  heal(amount: number): void {
    if (this.isKO) return;
    this.health.heal(amount);
  }

  protected flash(): void {
    this.scene.tweens.add({
      targets: this.bodySprite ?? this.bodyImage ?? this.bodyRect,
      alpha: { from: 0.3, to: 1 },
      duration: 120,
    });
  }

  protected knockOut(): void {
    this.isKO = true;
    this.bodyRect.setAngle(90);
    if (this.bodyImage) {
      // Skip rotation when a ko pose sprite is available — the pose already
      // depicts the knocked-down posture; setAngle(90) would misalign it.
      if (!hasKoPoseSprite(this._poseAssetIds, spriteAtlas)) {
        this.bodyImage.setAngle(90);
      }
    }
    // Animated sprite has no ko-sheet yet, so rotate it flat like the rect.
    if (this.bodySprite) this.bodySprite.setAngle(90);
    this.setAlpha(0.6);
  }

  revive(): void {
    this.isKO = false;
    this.hitstun = 0;
    // Clear any one-shot (super/special) clip lock left over from death: a hero
    // KO'd mid-super never reaches applyPoseTexture's hurt/ko release (update()
    // returns early while isKO), so the lock would otherwise carry into the
    // revived life and freeze the sprite on its old super frame.
    this.oneShotAnimLock = 0;
    this._currentAnimKey = null;
    this.bodyRect.setAngle(0);
    if (this.bodyImage) this.bodyImage.setAngle(0).setAlpha(1);
    if (this.bodySprite) this.bodySprite.setAngle(0).setAlpha(1);
    this.setAlpha(1);
    this.health.reset();
  }

  /** Face a horizontal direction (mirrors the body). */
  setFacing(dir: 1 | -1): void {
    this.facing = dir;
    this.bodyRect.scaleX = dir;
    if (this.bodyImage) this.bodyImage.setFlipX(dir === -1);
    if (this.weaponIndicatorRect) {
      this.weaponIndicatorRect.x = dir * (this.bodyW * 0.4 + 6);
    }
  }

  /**
   * Equip a weapon: store state and add a coloured visual indicator to the
   * entity container (pending sprite art).
   */
  equipWeapon(weapon: WeaponState): void {
    this.dropWeapon(); // ensure any prior weapon is cleared first
    this.heldWeapon = weapon;
    const rect = this.scene.add.rectangle(
      this.facing * (this.bodyW * 0.4 + 6),
      -this.bodyH * 0.15,
      28,
      8,
      weapon.def.color,
    );
    this.add(rect);
    this.weaponIndicatorRect = rect;
  }

  /**
   * Remove the held weapon (drop or break). Destroys the visual indicator.
   */
  dropWeapon(): void {
    this.heldWeapon = null;
    if (this.weaponIndicatorRect) {
      this.remove(this.weaponIndicatorRect, true);
      this.weaponIndicatorRect = null;
    }
  }

  /** Per-frame logic. Param-less for now (fixed-step); delta added when needed. */
  abstract update(): void;
}
